import {
  TRANSITION_VELOCITY_CPS,
  dtwAlign,
  pitchVelocityCents,
  scoreAlignment,
  smoothPitchHz,
} from '@swarasense/shared';
import type {
  AlignmentSummary,
  GamakaProfileEntry,
  NotationTimeline,
  NoteScore,
  PracticeLevel,
  RecordedSample,
  SungFrame,
} from '@swarasense/shared';
import { celebrate } from './celebrate.js';
import { getGrownupPref, setGrownupPref } from './grownup-pref.js';
import { swaraLabel } from './notation-lane.js';

/**
 * Task 2 (targeted drills) — the practice-mode helpers that don't need
 * `app.ts`'s big shared closure (audio engines, the store, the ribbon):
 * the pure beat clock the follow-along cursor is driven from, and (Task 3)
 * the gentle per-note review rendered after Stop.
 */

/** Elapsed wall-clock time → elapsed beats, at a fixed tempo. Pure (no
 * `Date.now`/`performance.now` inside) so `app.ts`'s rAF loop can pass in
 * whatever elapsed time it already computed and this stays trivially
 * testable. `tempoBpm <= 0` returns 0 rather than dividing by zero/NaN — a
 * defensive floor, not an expected input. */
export function elapsedBeats(elapsedMs: number, tempoBpm: number): number {
  if (tempoBpm <= 0) return 0;
  return (elapsedMs / 1000) * (tempoBpm / 60);
}

/**
 * Clamps a continuously-advancing beat count to `[0, totalBeats]` — the
 * follow-along cursor advances ONCE through the drill and holds at the end,
 * rather than wrapping back to 0 (the previous `loopedBeat` behaviour).
 *
 * Looping was a real bug, not a feature: `dtwAlign`/`scoreAlignment` model a
 * SINGLE monotonic pass over `timeline.notes`, so once a diligent child sang
 * a short drill a second time before hitting Stop, the 2nd pass's frames
 * piled onto the last note(s) in the DP and scored notes the child sang
 * correctly as off/wrong. Looping also let the audible metronome (which
 * keeps cycling its own thaalam grid) and the visual cursor permanently
 * diverge once the cursor wrapped mid-cycle. See `phraseComplete` for the
 * matching "has the drill finished" signal `app.ts`'s practice clock uses to
 * auto-stop the take instead of looping forever.
 */
export function clampedBeat(beats: number, totalBeats: number): number {
  if (totalBeats <= 0) return 0;
  return Math.min(Math.max(beats, 0), totalBeats);
}

/** True once the follow-along cursor has reached (or passed) the end of the
 * phrase — `app.ts`'s practice clock uses this as the signal to stop
 * advancing and auto-stop the take into the gentle review, so a drill ends
 * itself as a natural "you finished!" moment instead of only ending when the
 * user remembers to hit Stop. */
export function phraseComplete(beats: number, totalBeats: number): boolean {
  return totalBeats > 0 && beats >= totalBeats;
}

export interface PracticeReviewDeps {
  timeline: NotationTimeline;
  recSamples: RecordedSample[];
  saHz: number;
  level: PracticeLevel;
  gamakaProfile: Record<string, GamakaProfileEntry>;
  /** The drill's ACTUAL target swara (the raw 12-symbol `generateDrill` was
   * called with, e.g. from `app.ts`'s `store.drillTargetSwara`) — named
   * directly in the celebratory headline. Passed explicitly rather than
   * inferred from `timeline.notes.at(-1)`: every anchor+approach template
   * today happens to end on the target, but that's a template detail, not a
   * `NotationTimeline` contract, so this review shouldn't re-derive it. */
  targetSwara: string;
  /** Re-run the same drill from the top. */
  onTryAgain: () => void;
  /** Leave practice mode (back to free practice). */
  onDone: () => void;
}

/**
 * `RecordedSample[]` (`{t, freqHz}`, ms since take start) → `SungFrame[]`
 * (`{tMs, cents, transition}`) for `dtwAlign`. Mirrors `summary-charts.ts`'s
 * `driftSeries` frame-by-frame exactly (same `smoothPitchHz` smoothing, same
 * `TRANSITION_VELOCITY_CPS` glide threshold) so a drill take is treated
 * identically to a free-practice one — only the alignment target differs.
 * `cents` is left unfolded (raw Sa-relative, any octave); `dtwAlign`/
 * `scoreAlignment` fold internally, same as every other cents consumer in
 * this app. Empty input yields `[]` rather than throwing on `sorted[0]`.
 */
export function buildSungFrames(recSamples: readonly RecordedSample[], saHz: number): SungFrame[] {
  if (recSamples.length === 0) return [];
  const sorted = [...recSamples].sort((a, b) => a.t - b.t);
  const frames: SungFrame[] = [];
  let prev: { hz: number; t: number } | null = null;
  for (let i = 0; i < sorted.length; i++) {
    const s = sorted[i]!;
    const smoothHz = smoothPitchHz(sorted, i, saHz);
    const cents = 1200 * Math.log2(smoothHz / saHz);
    const transition =
      prev !== null &&
      Math.abs(pitchVelocityCents(prev.hz, prev.t / 1000, smoothHz, s.t / 1000)) >
        TRANSITION_VELOCITY_CPS;
    prev = { hz: smoothHz, t: s.t };
    frames.push({ tMs: s.t, cents, transition });
  }
  return frames;
}

/** A note "landed" — the gentle review's filled/soft-check mark — when it was
 * actually sung (not `missed`) AND graded excellent/good. Off-pitch or
 * missed notes both get the same quiet hollow mark: the brief's "never a
 * harsh cross" applies equally to a wrong note and a skipped one, so there's
 * no separate "worse" visual for either. */
export function noteLanded(note: NoteScore): boolean {
  return !note.missed && (note.band === 'excellent' || note.band === 'good');
}

/** The mark's accessible name — distinguishes landed vs. not WITHOUT relying
 * on the shape/colour alone (screen readers, and the brief's own "G — landed"
 * / "P — try again" examples). */
export function noteAriaLabel(note: NoteScore, octave: -1 | 0 | 1): string {
  const label = swaraLabel(note.swara, octave);
  return noteLanded(note) ? `${label} — landed` : `${label} — try again`;
}

export interface DrillRunSummary {
  headline: string;
  /** Whether this run is good enough to fire `celebrate()` on — mirrors
   * `results-panel.ts`'s `wentWell`/`CHILD_CELEBRATE_PCT`, at a lower bar: a
   * drill is a handful of notes (often 4-6), so one miss swings `accuracyPct`
   * much harder than a full multi-minute free-practice take does. */
  wentWell: boolean;
}

/** Share (0-100) of a drill's notes landed at/above which the run reads as
 * "mostly nailed it" — see `DrillRunSummary.wentWell`'s doc for why this is
 * lower than the free-practice results panel's session-wide 75%. */
const DRILL_CELEBRATE_PCT = 60;

/**
 * The gentle review's ONE warm summary sentence — words only, never a
 * number (the brief's "NO per-note percentages" extends to this headline
 * too; the numeric `accuracyPct` this is derived from lives only in the
 * Grown-up section). `targetSwara` is the drill's own focus note (the same
 * "second scale symbol"/anchor+approach target `app.ts`'s
 * `enterPracticeMode` already names in the practice banner), named directly
 * in the celebratory case so the praise is specific, not generic.
 */
export function summarizeDrillRun(notes: readonly NoteScore[], targetSwara: string): DrillRunSummary {
  const anySung = notes.some((n) => !n.missed);
  if (!anySung) {
    return { headline: "Didn't catch much that time — want to try again?", wentWell: false };
  }
  const landed = notes.filter(noteLanded).length;
  const landedPct = notes.length ? (100 * landed) / notes.length : 0;
  if (landedPct >= DRILL_CELEBRATE_PCT) {
    return {
      headline: `You nailed most of it — ${targetSwara} is getting steadier!`,
      wentWell: true,
    };
  }
  return { headline: "Good try — let's go again!", wentWell: false };
}

/** Swara names + coaching copy below come from our own seed data + curriculum
 * output (never user input), so plain interpolation into innerHTML is fine —
 * same trust boundary the rest of the app uses for this data. Escaped anyway
 * as cheap insurance. */
function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

const BAND_LABEL: Record<NoteScore['band'], string> = {
  excellent: 'Excellent',
  good: 'Good',
  off: 'Off',
  wrong: 'Wrong',
};

function signed(n: number): string {
  return `${n >= 0 ? '+' : ''}${Math.round(n)}`;
}

/** The Grown-up-only per-note detail table + summary numbers — everything
 * the brief keeps OUT of the child view: `centsOffset`, `timingOffsetMs`,
 * `accuracyPct`/`meanTimingMs`. Reuses the exact same `<table class="data-
 * table">` shape `results-panel.ts`'s sparkline fallback already uses, so no
 * new table styling was needed. */
function grownupDetailHtml(notes: readonly NoteScore[], summary: AlignmentSummary, timeline: NotationTimeline): string {
  const rows = notes
    .map((n, i) => {
      const note = timeline.notes[i];
      const label = escapeHtml(swaraLabel(n.swara, note?.octave ?? 0));
      const grade = n.missed ? 'Missed' : BAND_LABEL[n.band];
      const cents = n.missed ? '—' : `${signed(n.centsOffset)}¢`;
      const timing = n.missed ? '—' : `${signed(n.timingOffsetMs)}ms`;
      const gamaka = n.gamakaVerdict ?? '—';
      return `<tr><td>${i + 1}</td><td>${label}</td><td>${grade}</td><td>${cents}</td><td>${timing}</td><td>${escapeHtml(gamaka)}</td></tr>`;
    })
    .join('');
  return `
    <div class="metrics">
      <div class="metric"><div class="k">Accuracy</div><div class="val">${Math.round(summary.accuracyPct)}%</div></div>
      <div class="metric"><div class="k">Mean timing</div><div class="val">${signed(summary.meanTimingMs)}ms</div></div>
    </div>
    <div class="card">
      <h2>Per-note detail</h2>
      <div class="table-scroll">
        <table class="data-table">
          <caption>Per-note pitch and timing offsets for this take</caption>
          <thead><tr><th scope="col">Beat</th><th scope="col">Swara</th><th scope="col">Grade</th><th scope="col">Cents off</th><th scope="col">Timing</th><th scope="col">Gamaka</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </div>`;
}

/**
 * Task 3 (targeted drills): the GENTLE per-note review shown after Stop in
 * practice mode. Runs `dtwAlign` + `scoreAlignment` (Phase 5's shared
 * scoring, unchanged) against the drill's own `timeline`, then renders one
 * soft mark per note — a warm filled check for a landed note, a quiet hollow
 * outline for a missed/off one, NEVER a harsh cross and NEVER a number in
 * this default view — plus one warm summary sentence (`summarizeDrillRun`)
 * and a ✨ `celebrate()` when the run went well. Per-note cents/timing and
 * the numeric accuracy live ONLY inside the Grown-up disclosure below it,
 * reusing the exact same `swarasense.grownup` preference (`grownup-pref.ts`)
 * the free-practice results panel uses, so a grown-up's opt-in carries
 * across both surfaces.
 *
 * "Try again"/"Done" just call the caller-supplied `onTryAgain`/`onDone` —
 * `app.ts`'s `enterPracticeMode`/`exitPracticeMode` already own the actual
 * reset (`resetTake`, `stopPlayback`, clearing the lane), so this component
 * only wires the buttons, never duplicates that logic.
 */
export function renderPracticeReview(container: HTMLElement, deps: PracticeReviewDeps): void {
  const { timeline, recSamples, saHz, level, gamakaProfile, targetSwara } = deps;

  const sungFrames = buildSungFrames(recSamples, saHz);
  const alignment = dtwAlign(sungFrames, timeline, saHz);
  const { notes, summary } = scoreAlignment(alignment, timeline, [], saHz, level, gamakaProfile);
  const { headline, wentWell } = summarizeDrillRun(notes, targetSwara);

  const chips = notes
    .map((n, i) => {
      const note = timeline.notes[i];
      const label = escapeHtml(swaraLabel(n.swara, note?.octave ?? 0));
      const landed = noteLanded(n);
      const ariaLabel = escapeHtml(noteAriaLabel(n, note?.octave ?? 0));
      return `<li class="practice-review-note" aria-label="${ariaLabel}">
          <span class="mark ${landed ? 'landed' : 'try-again'}" aria-hidden="true"></span>
          <span class="swara">${label}</span>
        </li>`;
    })
    .join('');

  container.innerHTML = `
    <div class="reveal-note">— your practice take —</div>
    <div class="card practice-review child-summary">
      <p class="child-headline">${wentWell ? '✨ ' : ''}${escapeHtml(headline)}</p>
      <ul class="practice-review-notes" aria-label="Per-note review">${chips}</ul>
      <div class="practice-review-actions">
        <button class="btn" id="practice-try-again-btn" type="button">Try again</button>
        <button class="btn primary" id="practice-done-btn" type="button">Done</button>
      </div>
    </div>
    <div class="grownup-toggle-row">
      <button
        type="button"
        class="grownup-toggle"
        id="practice-grownup-toggle"
        aria-expanded="false"
        aria-controls="practice-grownup-section"
      >
        Grown-up view <span class="chev" aria-hidden="true">▾</span>
      </button>
    </div>
    <div class="grownup-section" id="practice-grownup-section" hidden>
      ${grownupDetailHtml(notes, summary, timeline)}
    </div>`;

  // Same threshold/anchor pattern as `results-panel.ts`'s "good take" ✨:
  // fires once, on the always-laid-out summary card, reduced-motion-aware
  // via `celebrate.ts` itself.
  if (wentWell) {
    const anchor = container.querySelector<HTMLElement>('.practice-review');
    if (anchor) celebrate(anchor);
  }

  const grownupToggle = container.querySelector<HTMLButtonElement>('#practice-grownup-toggle')!;
  const grownupSection = container.querySelector<HTMLElement>('#practice-grownup-section')!;
  function setGrownupExpanded(expanded: boolean): void {
    grownupToggle.setAttribute('aria-expanded', String(expanded));
    grownupSection.hidden = !expanded;
    grownupToggle.classList.toggle('open', expanded);
  }
  setGrownupExpanded(getGrownupPref());
  grownupToggle.addEventListener('click', () => {
    const next = grownupToggle.getAttribute('aria-expanded') !== 'true';
    setGrownupExpanded(next);
    setGrownupPref(next);
  });

  container
    .querySelector<HTMLButtonElement>('#practice-try-again-btn')
    ?.addEventListener('click', deps.onTryAgain);
  container.querySelector<HTMLButtonElement>('#practice-done-btn')?.addEventListener('click', deps.onDone);
}
